import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Menu, Form, Button, PageHeader, Layout, Spin, Row } from 'antd'
import {
	FormOutlined,
	NodeIndexOutlined,
	TeamOutlined,
	SettingOutlined,
} from '@ant-design/icons'
import {
	getTemplateDetail,
	editTemplateRequest,
	editTemplateTitle,
	getTemplateDownload,
	setDocPosted,
	onChangeConfig,
} from '~/states/modules/editTemplate'

import BreadCrumb from '~/components/breadCrumb'
import TemplateForm from './components/form'
import Workflow from './components/workflow'
import Signers from './components/signers'
import Styles from './components/styles'
import Text from './components/text'
import Config, { configSelector } from './components/config'

import styles from './index.module.scss'
import MainLayout from '~/components/mainLayout'

const EditTemplate = () => {
	const { data, loading, docPosted } = useSelector(
		({ editTemplate }) => editTemplate
	)
	const loggedUser = useSelector(({ session }) => session)
	const { id } = useHistory().location.state
	const history = useHistory()
	const edit = Number.isInteger(id)
	const dispatch = useDispatch()
	const [form] = Form.useForm()
	const [currentTab, setCurrentTab] = useState('form')
	const [inputsFilled, setInputsFilled] = useState({
		form: edit,
		workflow: true,
		text: edit,
		signers: true,
		config: true,
		styles: true,
	})
	const [files, postFiles] = useState([])
	const [widgetIndexes, setWidgetIndexes] = useState(0)
	const [templateType, setTemplateType] = useState('')
	const normalizedSigners = Array.isArray(data.signers)
		? { signers: data.signers, message: '', responsible_group: [] }
		: {
				signers: data.signers.signers || [],
				message: data.signers.message || '',
				responsible_group: data.signers.responsible_group || [],
			}

	const getWidgetIndexes = (values) => {
		setWidgetIndexes(values)
	}

	const editDOCX = () => {
		postFiles([{ uid: 'edit', name: 'Arquivo Atual' }])
	}

	const removeDoc = () => {
		dispatch(setDocPosted(false))
	}

	const handleNav = (e) => {
		switch (currentTab) {
			case 'form':
				setCurrentTab(e.key)
				break
			case 'workflow':
				setCurrentTab(e.key)
				break
			case 'signers':
				setCurrentTab(e.key)
				break
			case 'config':
				dispatch(onChangeConfig(configSelector(form, templateType)))
				setCurrentTab(e.key)
				break
			case 'styles':
				// dispatch(onChangeConfig(configSelector(form)))
				setCurrentTab(e.key)
				break
			default:
				setCurrentTab('form')
		}
	}

	const handleEditTitleButton = (title) => {
		dispatch(editTemplateTitle({ title }))
	}

	const setDownloadButton = () => {
		dispatch(getTemplateDownload({ id }))
	}

	// Checks if all fields are filled (all but form tab for now)
	const validate = () => {
		const redirect = (tab) => setCurrentTab(tab)
		data.form.forEach((page) => {
			if (page.title === '') {
				redirect('form')
			}
		})
		data.workflow.steps.forEach((step) => {
			if (step.title === '') {
				redirect('workflow')
				return false
			}
		})
		if (
			data.text === '' &&
			!files.length &&
			data.config.templateType !== 'upload'
		) {
			redirect('text')
			return false
		}
		normalizedSigners.signers.forEach((signingParty) => {
			if (signingParty.title === '') {
				redirect('signers')
				return false
			}
			if (Array.isArray(signingParty.signers)) {
				signingParty.signers.forEach((signer) => {
					if (signer.title === '' || signer.anchor[0].anchor_string === '') {
						redirect('signers')
						return false
					}
					signer.fields.forEach((field) => {
						if (field.variable === '') {
							redirect('signers')
							return false
						}
					})
				})
			}
		})

		return true
	}

	const onSubmit = () => {
		if (currentTab === 'config') {
			dispatch(onChangeConfig(configSelector(form, templateType, data.team_id)))
		}
		const isValid = validate()
		if (isValid) {
			dispatch(editTemplateRequest({ id, files, history }))
		}
	}

	useEffect(() => {
		if (Number.isInteger(id)) {
			dispatch(getTemplateDetail({ id, editDOCX }))
		}
	}, [dispatch, id])

	const disabledTitleTemplate = data.title.trim().length === 0

	const isSaveDisabled = data.config.templateType !== 'upload'

	return (
		<MainLayout>
			{loggedUser.is_company_admin ? (
				<Layout
					style={{ padding: '0', backgroundColor: '#fff', width: '100%' }}>
					<PageHeader>
						<Row justify="space-between">
							<BreadCrumb
								parent="Modelos"
								editable={true}
								current={data.title}
								onEdit={handleEditTitleButton}
								propsSize="500px"
							/>
							<Button
								form="createTemplate"
								key="button"
								type="primary"
								htmlType="submit"
								className={styles.button}
								disabled={
									isSaveDisabled &&
									(!Object.values(inputsFilled).every(Boolean) ||
										disabledTitleTemplate)
								}>
								Salvar
							</Button>
						</Row>
					</PageHeader>

					<Menu
						onClick={handleNav}
						selectedKeys={[currentTab]}
						mode="horizontal"
						style={{ display: 'flex' }}>
						<Menu.Item
							key="form"
							className={!inputsFilled.form ? styles.empty : undefined}
							icon={<FormOutlined />}>
							Formulário
						</Menu.Item>
						<Menu.Item
							key="workflow"
							className={!inputsFilled.workflow ? styles.empty : undefined}
							icon={<NodeIndexOutlined />}>
							Workflow
						</Menu.Item>
						<Menu.Item
							key="signers"
							className={!inputsFilled.signers ? styles.empty : undefined}
							icon={<TeamOutlined />}>
							Signatários
						</Menu.Item>
						<Menu.Item
							key="config"
							className={!inputsFilled.config ? styles.empty : undefined}
							icon={<SettingOutlined />}>
							Configurações
						</Menu.Item>
						{loggedUser.is_admin && (
							<Menu.Item
								key="styles"
								className={!inputsFilled.styles ? styles.empty : undefined}
								icon={<SettingOutlined />}>
								Estilos
							</Menu.Item>
						)}
					</Menu>

					<Layout className={styles.content}>
						{loading ? (
							<Spin spinning={loading} className={styles.spin} />
						) : (
							<Form
								style={{ height: '100%' }}
								id="createTemplate"
								form={form}
								layout="horizontal"
								hideRequiredMark
								onFinish={onSubmit}>
								{currentTab === 'form' && (
									<div className={styles.form}>
										<TemplateForm
											data={data}
											setInputsFilled={setInputsFilled}
											getWidgetIndexes={getWidgetIndexes}
										/>
										<Text
											data={data.text}
											ckeditorStyles={data.ckeditorStyles}
											ckeditorCss={data.ckeditorCss}
											files={files}
											updateFile={postFiles}
											setDownloadButton={setDownloadButton}
											setInputsFilled={setInputsFilled}
											docPosted={docPosted}
											removeDoc={removeDoc}
											widgetIndexes={widgetIndexes}
											templateType={data.config.templateType}
										/>
									</div>
								)}
								{currentTab === 'workflow' && <Workflow form={form} />}
								{currentTab === 'signers' && (
									<Signers signingParties={data.signers} />
								)}
								{currentTab === 'config' && (
									<Config
										teamId={data.team_id}
										config={data.config}
										form={form}
										onTemplateChange={setTemplateType}
										files={files}
										editor_text={data.text}
									/>
								)}
								{currentTab === 'styles' && (
									<Styles
										data={data.text}
										ckeditorStyles={data.ckeditorStyles}
										ckeditorCss={data.ckeditorCss}
										files={files}
										updateFile={postFiles}
										setDownloadButton={setDownloadButton}
										setInputsFilled={setInputsFilled}
										docPosted={docPosted}
										removeDoc={removeDoc}
										widgetIndexes={widgetIndexes}
									/>
								)}
							</Form>
						)}
					</Layout>
				</Layout>
			) : (
				<h2>Você não tem permissão para acessar esse recurso.</h2>
			)}
		</MainLayout>
	)
}

export default EditTemplate
